/* eslint-disable no-nested-ternary */
import { preload, useMemoRef, useStyles } from '@platform/react/hook'
import { Grid, useMediaQuery } from '@mui/material'
import SimpleCard from 'ui/Element/Card'
import ErrorBoundary from 'ui/Error'
import { Suspense } from 'react'

/**
 * Helper function to render the list of cards
 *
 * @param {Object} value              value for the card
 * @param {number} width              width of the card
 * @param {Object} icon               icon options
 * @param {string} background         background color to use
 * @param {React.ReactNode} Template  component to render the card
 * @returns {*[]|*}
 * @private
 */
const _content = ({ value, width, icon, background, Template = SimpleCard }) => (
  !value?.length ? [] : value.map((card, i) => (
    <Grid
      item
      xs={12}
      padding={1}
    >
      <Template
        key={i}
        xs={12}
        sm={width}
        md={width}
        lg={width}
        xl={width}
        background={background}
        icon={card?.icon || icon}

        label={card?.name || '-'}
        labelVariant={'14/bold'}
        subLabel={card?.group || '-'}
        subLabelVariant={'12/regular'}

        parentClasses={{ root: { padding: 0 } }}
      />
    </Grid>
  ))
)

const styles = theme => ({
  label: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(theme.typography.fontSize),
  },
})

/**
 * Card List Component
 *
 * Displays a list of {@link SimpleCard} or {@link SmallCard} components
 *
 * @param {Object[]} value            value of each item
 * @param {string} template           card component to use
 * @param {Object} props              additional props
 * @param {number} [props.cardWidth]  the width (in terms of grid spacing) for each card
 * @returns {JSX.Element}
 */
const CardList = ({ value, template, ...props }) => {
  const classes = useStyles(styles)()

  // Filter out objects with same 'name' property
  const filteredValue = [...new Map(value.map(item => [item.name, item])).values()]

  const { xs, sm, md, lg, xl, label, ...options } = props
  const { cardWidth: width = 'auto', icon, background, row = true } = options
  const isXs = useMediaQuery(t => t.breakpoints.down('sm'))

  const Template = preload(template)

  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Grid
          item
          container
          xs={xs}
          sm={sm}
          md={md}
          lg={lg}
          xl={xl}
        >
          <Grid
            item
            xs={12}
            className={classes.label}
          >
            {label}
          </Grid>
          <Grid
            item
            container
            xs={12}
            sx={{
              gap: '0.25rem',
              flexDirection: row
                ? 'row'
                : isXs
                  ? 'row'
                  : 'column',
            }}
          >
            {_content({
              value: filteredValue,
              width,
              icon,
              background,
              Template,
            })}
          </Grid>
        </Grid>
      </Suspense>
    </ErrorBoundary>
  )
}

export default useMemoRef(CardList, props => [props.value])
